// Status chips for the listing-request list + detail views: the request's one
// status chip, and each declared owner's onboarding and signing status. Every
// value is derived server-side (seller-service, kept identical with
// admin-service); this file only names them.
import { STAGE_META_FALLBACK, type LifecycleTone } from './listingRequestLifecycle'

// The one status chip a request shows (ListingRequestDisplayStatus). The server
// derives it — it is finer than the stage ("Price Proposed" is an open proposal
// on a request in review, "Awaiting Sliceal Signature" a signatory state) — and
// admin-web renders the same value, so both portals name a request alike.
// Never re-derive it here from `requestStatus`.
export type ListingRequestDisplayStatus =
  | 'draft'
  | 'under_review'
  | 'price_proposed'
  | 'revision_requested'
  | 'owners_invited'
  | 'onboarding_in_progress'
  | 'ready_to_sign'
  | 'awaiting_signatures'
  | 'awaiting_sliceal_signature'
  | 'agreement_declined'
  | 'final_checks'
  | 'approved_for_property_setup'
  | 'rejected'
  | 'withdrawn'

export interface StatusChip {
  label: string
  tone: LifecycleTone
}

// Wording from the product spec (Seller Portal Content, status chips), matching
// admin-web. The tone gives each stage its own hue (Figma "Sell Requests — List
// View"), so a seller scanning the table can tell stages apart without reading:
// review amber, owner onboarding brand, agreement blue, final approval green.
// "Agreement Declined" is not in the spec's list — it is the chip for an owner
// having declined, while our team re-issues the agreement.
const DISPLAY_STATUS_CHIPS: Record<ListingRequestDisplayStatus, StatusChip> = {
  draft: { label: 'Draft', tone: 'neutral' },
  under_review: { label: 'Under Review', tone: 'warning' },
  price_proposed: { label: 'Price Proposed', tone: 'warning' },
  revision_requested: { label: 'Revision Requested', tone: 'warning' },
  owners_invited: { label: 'Owners Invited', tone: 'primary' },
  onboarding_in_progress: { label: 'Onboarding in Progress', tone: 'primary' },
  ready_to_sign: { label: 'Ready to Sign', tone: 'info' },
  awaiting_signatures: { label: 'Awaiting Signatures', tone: 'info' },
  awaiting_sliceal_signature: { label: 'Awaiting Sliceal Signature', tone: 'info' },
  agreement_declined: { label: 'Agreement Declined', tone: 'warning' },
  final_checks: { label: 'Final Checks in Progress', tone: 'success' },
  approved_for_property_setup: { label: 'Approved for Property Setup', tone: 'success' },
  rejected: { label: 'Rejected', tone: 'danger' },
  withdrawn: { label: 'Withdrawn', tone: 'neutral' },
}

export interface StatusChipSource {
  displayStatus: string
  displayProgressDone: number | null
  displayProgressTotal: number | null
}

/** "Onboarding in Progress (1 of 2)" — the count rides along whenever the server sent one. */
export function statusChipOf(source: StatusChipSource): StatusChip {
  const chip = DISPLAY_STATUS_CHIPS[source.displayStatus as ListingRequestDisplayStatus] ?? {
    label: STAGE_META_FALLBACK.label,
    tone: 'neutral',
  }
  const { displayProgressDone: done, displayProgressTotal: total } = source
  return done !== null && total !== null
    ? { ...chip, label: `${chip.label} (${done} of ${total})` }
    : chip
}

// One declared owner's onboarding (OwnerOnboardingStatus), for the ownership
// table. Wording from the product spec, matching admin-web's Owners tab.
const OWNER_ONBOARDING_CHIPS: Record<string, StatusChip> = {
  invited: { label: 'Invited', tone: 'neutral' },
  registered: { label: 'Registered', tone: 'info' },
  kyc_in_review: { label: 'KYC in Review', tone: 'warning' },
  verified: { label: 'Verified', tone: 'success' },
  action_needed: { label: 'Action Needed', tone: 'danger' },
  invite_expired: { label: 'Invite Expired', tone: 'warning' },
}

// One owner's signature on the agreement out for signing (OwnerSigningStatus).
const OWNER_SIGNING_CHIPS: Record<string, StatusChip> = {
  pending: { label: 'Pending', tone: 'warning' },
  signed: { label: 'Signed', tone: 'success' },
}

// Null means the stage has not reached this owner yet — nothing has started. A
// value this build doesn't know is something, so it must not read as nothing.
const OWNER_STATUS_NOT_STARTED: StatusChip = { label: 'Not started', tone: 'neutral' }
const OWNER_STATUS_UNKNOWN: StatusChip = { label: 'In progress', tone: 'neutral' }

function ownerChipOf(chips: Record<string, StatusChip>, status: string | null): StatusChip {
  if (status === null) return OWNER_STATUS_NOT_STARTED
  return chips[status] ?? OWNER_STATUS_UNKNOWN
}

export function ownerOnboardingChipOf(status: string | null): StatusChip {
  return ownerChipOf(OWNER_ONBOARDING_CHIPS, status)
}

export function ownerSigningChipOf(status: string | null): StatusChip {
  return ownerChipOf(OWNER_SIGNING_CHIPS, status)
}
